import { useEffect, useState } from 'react';

/**
 * Is this a desktop-sized viewport?
 *
 * The app is mobile-first and everything below this width keeps the phone build
 * — the 460px `.app-frame`, the bottom tab bar, the sheets. At and above it the
 * desktop shell takes over: a sidebar carrying every module, tables instead of
 * stacked cards, drawers instead of bottom sheets.
 *
 * 1100px, not 1024: at 1024 the three-pane record screens (tabs + a 380px
 * summary rail) have under 600px left for the tab body, which is narrower than
 * the tables inside it. 1100 is the first width where the desktop layout is
 * actually better than the phone one rather than merely different — a tablet in
 * portrait stays on the build designed for a thumb.
 *
 * Kept as a hook rather than a CSS breakpoint because the two builds are
 * different component trees, not one tree restyled.
 */
const QUERY = '(min-width: 1100px)';

export default function useIsDesktop() {
  const [is, setIs] = useState(() => (typeof window === 'undefined' ? false : window.matchMedia(QUERY).matches));

  useEffect(() => {
    const mq = window.matchMedia(QUERY);
    const on = (e) => setIs(e.matches);
    // addEventListener is the modern API; addListener is the Safari <14 fallback.
    if (mq.addEventListener) mq.addEventListener('change', on);
    else mq.addListener(on);
    setIs(mq.matches);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', on);
      else mq.removeListener(on);
    };
  }, []);

  return is;
}
